import React, { FC, useState } from 'react';
import { MAIN_SITE_LINK } from '../../../constants';
import { redirect } from '../../../shared/helpers';
import {
    SearchSuggestionsTownsAndCitiesWrapper,
    TownsAndCitiesHeadlineWrapper,
    SectionWrapper,
    Section,
    LinkWrapper,
    SectionIcon,
    IconAndHeadingWrapper,
    SectionHeading,
    Divider,
    StyledButtonLinkWithIcon,
    StyledButton,
    StyledLabelForSale,
    StyledLabelToRent,
    SectionForSale,
    SectionToRent,
    StyledInputToRent,
    StyledInputForSale,
    StyledTabsContainer
} from './SearchSuggestionsTownsAndCities.styles';
import {
    trackBrowseAllCitiesTownsUK,
    trackSeeAllUKForSale,
    trackSeeAllUKToRent,
    trackIndividualLocationForSale,
    trackIndividualLocationToRent
} from './SearchSuggestionsTownsAndCities.tracking';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import homepageTranslations from 'public/locales/en/homepage.json';
import { Button, Heading, Icon, Text } from '@blueprint/ui';
import { SearchSuggestionsTownsAndCitiesProps } from './SearchSuggestionsTownsAndCities.types';
import CityIcon from '../../../../public/Icons/City_icon.png';
import CoastIcon from '../../../../public/Icons/Coast_icon.png';
import CountrysideIcon from '../../../../public/Icons/Countryside_icon.png';
import PopularLocationsIcon from '../../../../public/Icons/Popular_locations_icon.png';

const t = getTranslator(homepageTranslations);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateCity = (area: any, index: number) => {
    const { items } = area;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const translatedItems = items.map((item: any, innerIndex: number) => {
        return {
            ...item,
            headline: t(
                `towns_cities.areas[${index}].items[${innerIndex}].headline`
            )
        };
    });

    return {
        headline: t(`towns_cities.areas[${index}].headline`),
        sub_headline: t(`towns_cities.areas[${index}].sub_headline`),
        items: translatedItems
    };
};

const translateTownsAndCities = () => {
    const { areas } = homepageTranslations['towns_cities'];

    return {
        support_text_for_sale: t('towns_cities.support_text_for_sale'),
        support_text_to_rent: t('towns_cities.support_text_to_rent'),
        headline: t('towns_cities.headline'),
        cta: t('towns_cities.cta'),
        cta_UK_to_rent: t('towns_cities.cta_UK_to_rent'),
        cta_UK_for_sale: t('towns_cities.cta_UK_for_sale'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        areas: areas.map((area: any, index: number) =>
            translateCity(area, index)
        )
    };
};
const translatedTownsAndCities = translateTownsAndCities();

const sectionIcon = (headline: string) => {
    if (headline === 'In the city') {
        return CityIcon;
    } else if (headline === 'On the coast') {
        return CoastIcon;
    } else if (headline === 'Rural and countryside') {
        return CountrysideIcon;
    } else {
        return PopularLocationsIcon;
    }
};

const buildForSaleLink = () => {
    redirect(`${MAIN_SITE_LINK}/for-sale/property/uk/`);
};

const buildToRentLink = () => {
    redirect(`${MAIN_SITE_LINK}/to-rent/property/uk/`);
};

const buildSitemapLink = () => {
    redirect(`${MAIN_SITE_LINK}/sitemap/`);
};

export const SearchSuggestionsTownsAndCities: FC<SearchSuggestionsTownsAndCitiesProps> = () => {
    enum Tab {
        ForSale = 'for-sale-tab',
        ToRent = 'to-rent-tab'
    }

    const [selectedTabId, setSelectedTabId] = useState(Tab.ForSale);

    return (
        <SearchSuggestionsTownsAndCitiesWrapper>
            <TownsAndCitiesHeadlineWrapper>
                <Heading size="3">{translatedTownsAndCities.headline}</Heading>
                <Button
                    variant="tertiary"
                    data-testid="explore_more-cta-desktop"
                    onClick={() => {
                        buildSitemapLink();
                        trackBrowseAllCitiesTownsUK();
                    }}
                >
                    {translatedTownsAndCities.cta}
                </Button>
            </TownsAndCitiesHeadlineWrapper>

            <StyledTabsContainer>
                <StyledInputForSale
                    name="tabs_towns"
                    type="radio"
                    id={Tab.ForSale}
                    onChange={() => setSelectedTabId(Tab.ForSale)}
                    checked={selectedTabId === Tab.ForSale}
                />
                <StyledLabelForSale htmlFor={Tab.ForSale}>
                    For Sale
                </StyledLabelForSale>
                <SectionForSale>
                    {translatedTownsAndCities.areas.map(
                        (area, index: number) => {
                            const forSale = area.items.filter(
                                (i: { type: string }) => i.type === 'for-sale'
                            );
                            return (
                                <>
                                    <SectionWrapper key={index}>
                                        <SectionHeading>
                                            <SectionIcon>
                                                <img
                                                    src={sectionIcon(
                                                        area.headline
                                                    )}
                                                    alt=""
                                                />
                                            </SectionIcon>
                                            <IconAndHeadingWrapper>
                                                <Heading size="5">
                                                    {area.headline}
                                                </Heading>
                                                <Text size="8">
                                                    {area.sub_headline}
                                                </Text>
                                                {area.headline ===
                                                'Popular locations' ? (
                                                    <StyledButton
                                                        variant="tertiary"
                                                        data-testid="explore_more-cta-desktop"
                                                        onClick={() => {
                                                            buildForSaleLink();
                                                            trackSeeAllUKForSale();
                                                        }}
                                                    >
                                                        {
                                                            translatedTownsAndCities.cta_UK_for_sale
                                                        }
                                                    </StyledButton>
                                                ) : null}
                                            </IconAndHeadingWrapper>
                                        </SectionHeading>
                                        <Section key={index}>
                                            {forSale.slice(0, 3).map(
                                                (
                                                    item: {
                                                        headline: string;
                                                        url: string;
                                                    },
                                                    index: number
                                                ) => {
                                                    return (
                                                        <>
                                                            <LinkWrapper
                                                                key={index}
                                                            >
                                                                <StyledButtonLinkWithIcon
                                                                    variant="tertiary"
                                                                    data-testid={`city-living-link-${item.headline}`}
                                                                    href={
                                                                        item.url
                                                                    }
                                                                    onClick={() =>
                                                                        trackIndividualLocationForSale(
                                                                            item.headline
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        iconId="arrow_full_right"
                                                                        size="small"
                                                                    />
                                                                    {
                                                                        item.headline
                                                                    }{' '}
                                                                    {
                                                                        translatedTownsAndCities.support_text_for_sale
                                                                    }
                                                                </StyledButtonLinkWithIcon>
                                                            </LinkWrapper>
                                                        </>
                                                    );
                                                }
                                            )}
                                        </Section>

                                        <Section key={index}>
                                            {forSale.slice(3, 6).map(
                                                (
                                                    item: {
                                                        headline: string;
                                                        url: string;
                                                    },
                                                    index: number
                                                ) => {
                                                    return (
                                                        <>
                                                            <LinkWrapper
                                                                key={index}
                                                            >
                                                                <StyledButtonLinkWithIcon
                                                                    variant="tertiary"
                                                                    data-testid={`city-living-link-${item.headline}`}
                                                                    href={
                                                                        item.url
                                                                    }
                                                                    onClick={() =>
                                                                        trackIndividualLocationForSale(
                                                                            item.headline
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        iconId="arrow_full_right"
                                                                        size="small"
                                                                    />
                                                                    {
                                                                        item.headline
                                                                    }{' '}
                                                                    {
                                                                        translatedTownsAndCities.support_text_for_sale
                                                                    }
                                                                </StyledButtonLinkWithIcon>
                                                            </LinkWrapper>
                                                        </>
                                                    );
                                                }
                                            )}
                                        </Section>
                                    </SectionWrapper>
                                    {area.headline !== 'Popular locations' ? (
                                        <Divider />
                                    ) : null}
                                </>
                            );
                        }
                    )}
                </SectionForSale>

                <StyledInputToRent
                    name="tabs_towns"
                    type="radio"
                    id={Tab.ToRent}
                    onChange={() => setSelectedTabId(Tab.ToRent)}
                    checked={selectedTabId === Tab.ToRent}
                />
                <StyledLabelToRent htmlFor={Tab.ToRent}>
                    To Rent
                </StyledLabelToRent>
                <SectionToRent>
                    {translatedTownsAndCities.areas.map(
                        (area, index: number) => {
                            const toRent = area.items.filter(
                                (i: { type: string }) => i.type === 'to-rent'
                            );
                            return (
                                <>
                                    <SectionWrapper key={index}>
                                        <SectionHeading>
                                            <SectionIcon>
                                                <img
                                                    src={sectionIcon(
                                                        area.headline
                                                    )}
                                                    alt=""
                                                />
                                            </SectionIcon>
                                            <IconAndHeadingWrapper>
                                                <Heading size="5">
                                                    {area.headline}
                                                </Heading>
                                                <Text size="8">
                                                    {area.sub_headline}
                                                </Text>
                                                {area.headline ===
                                                'Popular locations' ? (
                                                    <StyledButton
                                                        variant="tertiary"
                                                        data-testid="explore_more-cta-desktop"
                                                        onClick={() => {
                                                            buildToRentLink();
                                                            trackSeeAllUKToRent();
                                                        }}
                                                    >
                                                        {
                                                            translatedTownsAndCities.cta_UK_to_rent
                                                        }
                                                    </StyledButton>
                                                ) : null}
                                            </IconAndHeadingWrapper>
                                        </SectionHeading>
                                        <Section key={index}>
                                            {toRent.slice(0, 3).map(
                                                (
                                                    item: {
                                                        headline: string;
                                                        url: string;
                                                    },
                                                    index: number
                                                ) => {
                                                    return (
                                                        <>
                                                            <LinkWrapper
                                                                key={index}
                                                            >
                                                                <StyledButtonLinkWithIcon
                                                                    variant="tertiary"
                                                                    data-testid={`city-living-link-${item.headline}`}
                                                                    href={
                                                                        item.url
                                                                    }
                                                                    onClick={() =>
                                                                        trackIndividualLocationToRent(
                                                                            item.headline
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        iconId="arrow_full_right"
                                                                        size="small"
                                                                    />
                                                                    {
                                                                        item.headline
                                                                    }{' '}
                                                                    {
                                                                        translatedTownsAndCities.support_text_to_rent
                                                                    }
                                                                </StyledButtonLinkWithIcon>
                                                            </LinkWrapper>
                                                        </>
                                                    );
                                                }
                                            )}
                                        </Section>
                                        <Section key={index}>
                                            {toRent.slice(3, 6).map(
                                                (
                                                    item: {
                                                        headline: string;
                                                        url: string;
                                                    },
                                                    index: number
                                                ) => {
                                                    return (
                                                        <>
                                                            <LinkWrapper
                                                                key={index}
                                                            >
                                                                <StyledButtonLinkWithIcon
                                                                    variant="tertiary"
                                                                    data-testid={`city-living-link-${item.headline}`}
                                                                    href={
                                                                        item.url
                                                                    }
                                                                    onClick={() =>
                                                                        trackIndividualLocationToRent(
                                                                            item.headline
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        iconId="arrow_full_right"
                                                                        size="small"
                                                                    />
                                                                    {
                                                                        item.headline
                                                                    }{' '}
                                                                    {
                                                                        translatedTownsAndCities.support_text_to_rent
                                                                    }
                                                                </StyledButtonLinkWithIcon>
                                                            </LinkWrapper>
                                                        </>
                                                    );
                                                }
                                            )}
                                        </Section>
                                    </SectionWrapper>
                                    {area.headline !== 'Popular locations' ? (
                                        <Divider />
                                    ) : null}
                                </>
                            );
                        }
                    )}
                </SectionToRent>
            </StyledTabsContainer>
        </SearchSuggestionsTownsAndCitiesWrapper>
    );
};
